import React, { useState, useEffect } from 'react';
import { Button, ButtonGroup, Card, Stack, CircularProgress } from '@mui/material';
import CountDown from '../../common/countdown/index';
import PropTypes from 'prop-types';
import { makePriceLabelRoundAE } from '../../../utils/helpers/make-price-label';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { AUCTION_TYPE, NUMBER, TOAST_OPTIONS } from '../../../constants/app-constants';
import { isTimePassed } from '../../../utils/helpers/secondsToHMS';
import { ConfirmationDialog } from '../../common/modal';
import { prepareGADataAndSendEvent } from '../../../tracking';
import { priceFormatter } from '../../../utils/helpers/price-formatter';

const BidModal = ({ isListing, endTime, bid, user, handleClose, handleCallBack = () => { }, setupToastConnect, submitPlaceBidConnect, fetchBidDetailsConnect, getAuctionDetailsConnect, handlePlaceBid = () => { } }) => {
    const { lastHighestBid, auctionId, appointmentId, thresholdBid, ocbV2, auctionType } = bid || {};
    const [bidDetails, setBidDetails] = useState({
        highestBid: lastHighestBid,
        dealerBid: lastHighestBid + NUMBER.FIVE_HUNDRED,
        stepRate: NUMBER.FIVE_HUNDRED
    });
    const [loading, setLoading] = useState(false);
    const [highAmountAlert, setHighAmountAlert] = useState(false);
    const { stepRate = NUMBER.FIVE_HUNDRED, highestBid, dealerBid = highestBid + stepRate } = bidDetails || {};

    const handleAddOn = (value) => {
        setBidDetails(() => ({ ...bidDetails, dealerBid: +dealerBid + +value }));
    };

    const fetchBid = () => {
        setLoading(true);
        fetchBidDetailsConnect(auctionId).then(res => {
            setBidDetails({ ...bidDetails, ...res, dealerBid: res?.highestBid ? res?.highestBid + res?.stepRate : dealerBid });
        }).finally(() => setLoading(false));
    };

    useEffect(() => {
        // Update whenever highest bid updates
        setBidDetails({ ...bidDetails, highestBid: lastHighestBid });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastHighestBid]);

    useEffect(() => {
        fetchBid();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubtraction = () => {
        if ((dealerBid - stepRate) < NUMBER.ZERO) return;
        setBidDetails(() => ({ ...bidDetails, dealerBid: dealerBid - stepRate }));
    };

    const handleAddition = () => {
        setBidDetails(() => ({ ...bidDetails, dealerBid: +dealerBid + stepRate }));
    };

    const isAmountHigh = () => {
        const percentValue = (NUMBER.FIVE / NUMBER.HUNDRED) * dealerBid;
        const diff = dealerBid - highestBid;
        return diff >= percentValue;
    };

    const handleSubmitBid = () => {
        if (isTimePassed(endTime)) {
            return;
        }
        if (dealerBid <= highestBid) {
            setupToastConnect({ message: 'Your bid should be more than current bid', type: TOAST_OPTIONS.ERROR });
            return;
        }
        if (isAmountHigh()) {
            setHighAmountAlert(true);
            return;
        }
        onAmountVerification();
    };

    const handleScrollTop = () => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    };

    const onAmountVerification = () => {
        const payload = { auctionId, amount: dealerBid };
        setLoading(true);
        prepareGADataAndSendEvent('submitBid', user, bid, 'Bid modal', { eventLabel: dealerBid });
        submitPlaceBidConnect(payload).then((res) => {
            if (isListing) {
                handleCallBack();
            } else {
                setBidDetails({ ...bidDetails, ...res });
                getAuctionDetailsConnect(appointmentId);
            }
            setupToastConnect({ message: 'Bid placed successfully!', type: TOAST_OPTIONS.SUCCESS });
            handleClose();
            handleScrollTop();
        }).catch((error) => {
            setupToastConnect({ message: error?.errorMessage || 'Bid place failed due to some internal error', type: TOAST_OPTIONS.ERROR });
        }).finally(() => setLoading(false));
    };

    const additionalValues = Array.from([NUMBER.ONE, NUMBER.TWO, NUMBER.THREE], x => x * stepRate) || [];
    const disableSubtraction = dealerBid <= (highestBid + stepRate);
    const disableSubmit = !ocbV2 && auctionType === AUCTION_TYPE.OCB;
    return (
        <Card sx={{ m: 2 }} className={`w-full relative ${loading && 'pointer-events-none'}`} >
            {loading && <CircularProgress size={'30px'} className='absolute bottom-8 right-7 z-10 text-border' />}
            <CloseOutlinedIcon onClick={handleClose} className='absolute top-2 right-2' />
            <Stack gap={2} className='p-6'>
                <div className='text-center flex items-center justify-center'>
                    <CountDown clsName='text-danger text-lg font-bold' isDate={true} date={endTime} />
                </div>
                <Stack direction={'row'} gap={2} justifyContent='space-between' alignItems={'center'}>
                    <div>Current Bid:</div>
                    <div className='font-bold'>{makePriceLabelRoundAE(highestBid)}</div>
                </Stack>
                {ocbV2 && auctionType === AUCTION_TYPE.OCB && <Stack direction={'row'} gap={2} justifyContent='space-between' alignItems={'center'}>
                    <div>One click buy:</div>
                    <div className='font-bold'>{makePriceLabelRoundAE(thresholdBid)}</div>
                </Stack>}
                <Stack direction={'row'} gap={2} justifyContent='space-between' alignItems={'center'}>
                    <div>Step Rate:</div>
                    <div className='font-bold'>{makePriceLabelRoundAE(stepRate)}</div>
                </Stack>
                <Stack direction={'column'} gap={2} className='mt-4'>
                    <Stack gap={2} direction='row' alignItems={'center'} justifyContent='space-between'>
                        <div className='font-bold text-lg'>Your Bid:</div>
                        <ButtonGroup variant="outlined" color='border' aria-label="outlined button group" className='text-center text-lg'>
                            <Button clas disabled={disableSubtraction} onClick={handleSubtraction}><RemoveOutlinedIcon className={`${!disableSubtraction && 'text-primary'}`} /></Button>
                            <Button className='text-primary font-bold text-lg'><span>AED</span><span className='ml-2' key={dealerBid}>{priceFormatter(dealerBid)}</span></Button>
                            <Button onClick={handleAddition}><AddOutlinedIcon className='text-primary' /></Button>
                        </ButtonGroup>
                    </Stack>
                    <Stack gap={2} direction='row' alignItems={'center'} justifyContent='space-between'>
                        {additionalValues.map((item, idx) => (
                            <Button onClick={handleAddOn.bind(null, item)} color='border' className='py-2 px-2 w-full text-center text-text ' variant='outlined' key={idx}>+{makePriceLabelRoundAE(item)}</Button>
                        ))}
                    </Stack>
                    <Stack gap={1} direction='column' alignItems={'center'} justifyContent='space-between' className='mt-2'>
                        <Button disabled={isTimePassed(endTime) || disableSubmit} variant="contained" onClick={handleSubmitBid} className={`py-3 w-full font-bold text-lg mt-4 capitalize ${loading && 'opacity-50'}`} color='secondary'>
                            Submit Bid
                        </Button>
                        {((ocbV2 && auctionType === AUCTION_TYPE.OCB) || disableSubmit) && <Button disabled={isTimePassed(endTime)} variant="contained" onClick={handlePlaceBid.bind(null, { isLiveAndOcb: true })} className={`py-3 w-full font-bold capitalize text-lg mt-4 ${loading && 'opacity-50'}`} color='secondary'>
                            {`Buy Now for ${makePriceLabelRoundAE(thresholdBid)}`}
                        </Button>}
                    </Stack>
                </Stack>
            </Stack>
            <ConfirmationDialog message='Bid amount is quite high! Please verify.' open={highAmountAlert} handleClose={setHighAmountAlert.bind(null, false)} handleVerified={onAmountVerification} />
        </Card >
    );
};
BidModal.propTypes = {
    endTime: PropTypes.any,
    user: PropTypes.string,
    isListing: PropTypes.bool,
    bid: PropTypes.object,
    handleClose: PropTypes.func,
    setupToastConnect: PropTypes.func,
    submitPlaceBidConnect: PropTypes.func,
    fetchBidDetailsConnect: PropTypes.func,
    getAuctionDetailsConnect: PropTypes.func,
    handleCallBack: PropTypes.func,
    handlePlaceBid: PropTypes.func
};

export default BidModal;
